
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { KanbanBoard } from '@/types/database';

interface BoardAccess {
  hasAccess: boolean;
  isLoading: boolean;
  error: any;
  isOwner: boolean;
  isCollaborator: boolean;
  board: KanbanBoard | null;
  accessibleBoards: KanbanBoard[];
  refreshBoards: () => void;
}

export function useWorkflowAccess(boardId?: string): BoardAccess {
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [board, setBoard] = useState<KanbanBoard | null>(null);
  const [accessibleBoards, setAccessibleBoards] = useState<KanbanBoard[]>([]);

  const checkAccess = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setHasAccess(false);
        setAccessibleBoards([]);
        return;
      }

      if (boardId) {
        // Check access for specific board
        const { data: boardData, error: boardError } = await supabase
          .from('kanban_boards')
          .select(`
            *,
            clients (
              id,
              name
            )
          `)
          .eq('id', boardId)
          .single();

        if (boardError) {
          throw boardError;
        }

        if (!boardData) {
          setHasAccess(false);
          return;
        }

        const formattedBoard = formatBoard(boardData);
        setBoard(formattedBoard);

        const visibleTo = boardData.visible_to;
        const isVisible = Array.isArray(visibleTo) && visibleTo.includes(user.id);
        const isBoardOwner = boardData.user_id === user.id;
        const isAllowed = isBoardOwner || isVisible;

        setIsOwner(isBoardOwner);
        setIsCollaborator(isVisible);
        setHasAccess(isAllowed);
      } else {
        // Get all accessible boards - use a Set to ensure uniqueness
        const { data: boardsData, error: boardsError } = await supabase
          .from('kanban_boards')
          .select(`
            *,
            clients (
              id,
              name
            )
          `)
          .eq('is_archived', false)
          .order('created_at', { ascending: false });

        if (boardsError) {
          throw boardsError;
        }

        if (boardsData) {
          // Use a Map to ensure unique boards by ID
          const uniqueBoardsMap = new Map();
          
          boardsData.forEach(board => {
            const isOwner = board.user_id === user.id;
            const visibleTo = board.visible_to;
            const isVisible = Array.isArray(visibleTo) && visibleTo.includes(user.id);
            
            // Only add if user has access and board is not already in the map
            if ((isOwner || isVisible) && !uniqueBoardsMap.has(board.id)) {
              uniqueBoardsMap.set(board.id, board);
            }
          });

          // Convert Map values to array and format
          const uniqueBoards = Array.from(uniqueBoardsMap.values());
          const formattedBoards = uniqueBoards.map(formatBoard);
          setAccessibleBoards(formattedBoards);
        } else {
          setAccessibleBoards([]);
        }
      }
    } catch (err: any) {
      setError(err);
      toast.error("Erro ao verificar acesso ao quadro");
      setHasAccess(false);
      setAccessibleBoards([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, [boardId]);

  const formatBoard = (board: any) => ({
    id: board.id,
    name: board.name,
    description: board.description,
    client_id: board.client_id,
    user_id: board.user_id,
    company_id: board.company_id,
    created_at: board.created_at,
    updated_at: board.updated_at,
    is_archived: board.is_archived,
    visible_to: board.visible_to || [],
    position: board.position || 0,
    public_token: board.public_token || null,
    is_public: board.is_public || false,
    public_access_expires_at: board.public_access_expires_at || null,
    client: {
      id: board.clients?.id || board.client_id,
      name: board.clients?.name || 'Cliente não encontrado'
    },
    clients: {
      id: board.clients?.id || board.client_id,
      name: board.clients?.name || 'Cliente não encontrado'
    }
  });

  return { 
    hasAccess, 
    isLoading, 
    error, 
    isOwner, 
    isCollaborator, 
    board, 
    accessibleBoards, 
    refreshBoards: checkAccess 
  };
}
